import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  extractPreappointmentQuestionnaireSummary,
  getPatientConsult,
} from '../../../../api';
import type { RootState } from '../../../../store';
import InputErrorMessage from '../components/InputErrorMessage';
import OnboardingIntro from '../components/onboading/OnboardingIntro';
import { useChatContext } from '../context/ChatProvider';
import { ChatModalType } from '../lib/types';
import '../components/onboading/onboarding.scss';
import PromptInput from '../components/PromptInput';

const CONSULT_ID_NOT_FOUND_MESSAGE = 'Consult ID not found. Please try again.';

const ChatOnboadingScreen = () => {
  const [isLoadingConsult, setIsLoadingConsult] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { firstName } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const {
    setCaremapprAppUserId,
    onboardingInput,
    setPreappointmentQuestionnaireSummary,
    setPatientConfirmationDetails,
    setOnboardingInput,
    setInitialPrompt,
    setModalType,
    setChatStartedWithConsultId,
  } = useChatContext();

  const isConsultId = (value: string) => /^[A-Za-z]\d{9}$/.test(value);

  const handleOnboardingInputChange = (value: string) => {
    setOnboardingInput(value);
    setInitialPrompt(null);
    setCaremapprAppUserId(null);
    setPreappointmentQuestionnaireSummary(null);
    setPatientConfirmationDetails(null);
    setChatStartedWithConsultId(false);
    setSubmitError(null);
  };

  const onStart = async () => {
    if (isLoadingConsult) {
      return;
    }

    const normalizedInput = onboardingInput.trim();
    if (!normalizedInput) {
      return;
    }

    setSubmitError(null);
    setOnboardingInput(normalizedInput);

    if (!isConsultId(normalizedInput)) {
      setCaremapprAppUserId(null);
      setPreappointmentQuestionnaireSummary(null);
      setPatientConfirmationDetails(null);
      setChatStartedWithConsultId(false);
      setInitialPrompt(normalizedInput);
      setModalType(ChatModalType.CONSENT);
      return;
    }

    setIsLoadingConsult(true);
    setInitialPrompt(null);
    setCaremapprAppUserId(null);
    setPreappointmentQuestionnaireSummary(null);
    setPatientConfirmationDetails(null);
    setChatStartedWithConsultId(false);

    try {
      const response = await getPatientConsult({ consultId: normalizedInput });
      const consult = response.data;
      const rawSummary = consult?.preappointmentQuestionnaireSummary ?? '';
      const summary = extractPreappointmentQuestionnaireSummary(rawSummary);
      if (!summary) {
        setSubmitError(CONSULT_ID_NOT_FOUND_MESSAGE);
        return;
      }

      setCaremapprAppUserId(
        typeof consult?.caremapprAppUserId === 'string' &&
          consult.caremapprAppUserId.trim()
          ? consult.caremapprAppUserId
          : null,
      );
      setPatientConfirmationDetails({
        appointmentDoctorName: String(consult?.appointmentDoctorName ?? ''),
        appointmentGpPractice: String(consult?.appointmentGpPractice ?? ''),
        appointmentDate: String(consult?.appointmentDate ?? ''),
        appointmentTime: String(consult?.appointmentTime ?? ''),
      });
      setPreappointmentQuestionnaireSummary(summary);
      setChatStartedWithConsultId(true);
      setModalType(ChatModalType.PATIENT);
    } catch (error) {
      setSubmitError(CONSULT_ID_NOT_FOUND_MESSAGE);
    } finally {
      setIsLoadingConsult(false);
    }
  };

  return (
    <div className="chat-onboarding d-flex justify-content-center">
      <div className="chat-onboarding__content">
        <OnboardingIntro
          name={firstName}
          description="Start a consult by asking a question below or by linking a patient who is registered with CareMappr. To link a patient, enter their Consult ID (found on the patient's CareMappr appointments screen) into the field to get started."
        />
        <PromptInput
          value={onboardingInput}
          placeholder="Enter a Consult ID or ask a question"
          onChange={handleOnboardingInputChange}
          onSubmit={onStart}
          disabled={isLoadingConsult}
          isLoading={isLoadingConsult}
        />
        <InputErrorMessage message={submitError} />
      </div>
    </div>
  );
};

export default ChatOnboadingScreen;
